/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The column-header line of the comparison list, from `xl` up — the 2026-10
 * redesign (docs/plan-vergelijker-resultaten-2026-09-30.md §3.3). It is laid
 * out by the same `.result-grid` template as every ResultRow, with the same
 * `rg-*` cell classes in the same order, and it mirrors the row's padding and
 * 1 px border (px-4 + a transparent border), so every column edge coincides.
 * Below `xl` there is no header (the card there names its own facts with
 * sr-only prefixes, and the results bar carries the filters and the sort):
 * with the Dutch column words a ten-column row at 1024 px leaves ~130 px for
 * the provider name, measured 2026-10-01, so the row starts where the name
 * keeps its room.
 *
 * The column words are the filters (2026-10-03, Jasper: "integrate the
 * filters in the new header bar for the compare results"; composition A of
 * the mockup). Looptijd, Voorwaarden and Slimme lader are column words that
 * open their own filter menu (FilterSelect, variant `column`). The two ⓘ
 * explanations moved INTO those menus — word + chevron + ⓘ does not fit the
 * measured columns —, so the Voorwaarden note is the foot of its menu and the
 * smart-charger explanation a button at the foot of its. The note
 * (`table.col_terms_note`, 2026-10-07) explains the column's two values and
 * their colours — when to cancel and what follows the term, green / orange /
 * grey — since the three glyphs left the row; its predecessor
 * `table.col_terms_info` explained the glyphs and stays frozen in i18n_admin.
 * The audience has no column: it leads the line, over the compare and
 * provider columns (one cell, `rg-lead`, which index.css spans from
 * select-start to name-end); "Vergelijk" and "Aanbieder" stay as sr-only
 * words, and the shortlist count lives in the tray only. At the end, over the two action columns (`rg-sort`), the engine's sort
 * control, its right edge on the Details column's. The loose pill row that sat
 * above the list from xl is gone: this is the one line of controls there.
 *
 * ONE grammar for every control on the line (2026-10-08, composition A of the
 * filter-bar mockup — Jasper: "better alignment and clearer intentionality of
 * the sort & filter option"). The mockup measured three grammars on one line:
 * 11 px pills with icons for the audience and the sort, 10 px capitals for the
 * filter words — styled exactly like the plain words Netto per jaar*,
 * Commissie and Uitbetaling, so a 10 px chevron was the only sign of a menu —
 * and a red "✕ 1" for "wissen"; the audience pill's first mark sat 8 px right
 * of the checkboxes under it, and "Sorteren" existed as an aria-label only.
 * Now every control is the `column` look (12 px semibold, 28 px on the 44 px
 * line, a word or "<label> <value>" + chevron, an outline on hover and while
 * open, a set filter emerald with a 6 px dot): the audience reads "Doelgroep:
 * Particulier", its text on the checkboxes' left edge; each filter word's
 * text on its column's left edge, the dot in the margin before it; the sort
 * "Sorteren: Hoogste opbrengst", its chevron over the Details chevrons. The
 * plain column words keep the line's 10 px capitals, so a filter no longer
 * looks like a label. "✕ Wissen" is a small emerald text link after the
 * audience while a filter it resets is set (the Score word that could stand
 * between the two left with the ratings, 2026-10-14).
 *
 * Nothing here owns a state. Every filter is the engine's — value, options,
 * setter — handed in as a `HeaderFilter`, and the results bar below xl renders
 * the same state with the same setters, so the two are two views of one
 * address. "Wissen" and the sort control are the engine's own elements,
 * handed in as nodes.
 *
 * Accessibility: a column word's name is `<word>: <selected label>`
 * (label-in-name) and its title the selected label; the audience's name is
 * "Doelgroep: <label>" and the sort's "Sorteren: <label>" — what they show.
 * The Voorwaarden note stays in the cell as sr-only text (a crawler reads the
 * baked DOM, and a menu is mounted only while open) and describes the word's
 * trigger. Without a Voorwaarden filter (no provider earns a feature) the
 * word stays a plain word with its ⓘ toggletip: a button that opens and
 * closes the note (click, Enter, Space; Escape and a click elsewhere close
 * it), closed at SSR. Every own button is a 24 px target (WCAG 2.5.8). The
 * "*" of "Netto per jaar*" points at the basis line above the list, which
 * starts with that asterisk.
 *
 * SSR-safe: no window, document or Date at render; every menu is closed in
 * the bake and nothing is set on the clean address.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { fillTemplate } from '../../lib/templateVars.js';
import { FilterSelect, type FilterOption } from './FilterSelect';

/** One of the engine's filters as the line renders it: the engine's state and setter. */
export interface HeaderFilter {
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** The value that counts as "not set": the word stays muted on it. */
  defaultValue: string;
  /** A multi-select (the looptijd): see FilterSelect's `multiple`. */
  multiple?: boolean;
  isSelected?: (optionValue: string) => boolean;
  /** What the control says for its value — a multi-select's selection in words. */
  valueText?: string;
}

export interface ResultHeaderRowProps {
  /** Doelgroep: the control that leads the line, over the compare and provider columns. */
  audience: HeaderFilter;
  /** Looptijd: the duration column's word. */
  duration: HeaderFilter;
  /** Slimme lader: the smart-charger column's word. */
  smartCharging: HeaderFilter;
  /**
   * Voorwaarden — null while no provider earns a feature (the engine's gate):
   * the word then stays a plain word with its ⓘ.
   */
  terms: HeaderFilter | null;
  /** Opens the smart-charger / MID explanation (the same modal as the row's panel). */
  onSmartChargerInfo: () => void;
  /** The engine's "✕ Wissen" (null while no filter is set): the lead cell's last control. */
  clearControl?: React.ReactNode;
  /** The engine's sort control ("Sorteren: …"): the only control of `rg-sort`, at the line's end. */
  sortControl: React.ReactNode;
}

/** The toggletip's note; there is one header row on the page. */
const TERMS_NOTE_ID = 'result-header-terms-note';
/** The sr-only Voorwaarden note, which describes the Voorwaarden word's trigger. */
const TERMS_INFO_ID = 'result-header-terms-info';

export const ResultHeaderRow: React.FC<ResultHeaderRowProps> = ({
  audience,
  duration,
  smartCharging,
  terms,
  onSmartChargerInfo,
  clearControl,
  sortControl,
}) => {
  const { t } = useLanguage();
  // The Voorwaarden toggletip (no terms filter only): client-only state, so it is closed in the bake.
  const [termsNoteOpen, setTermsNoteOpen] = useState(false);
  const termsTipRef = useRef<HTMLSpanElement>(null);
  const termsButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!termsNoteOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setTermsNoteOpen(false);
      termsButtonRef.current?.focus();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!termsTipRef.current?.contains(e.target as Node)) setTermsNoteOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [termsNoteOpen]);

  // What the Voorwaarden column's two values say and what their colours mean.
  const termsNote = t('table.col_terms_note');

  return (
    <div
      data-result-header
      // py-1.5 around the 28 px controls: a 44 px line with the band's
      // borders. The line's own type — 10 px black capitals — is the plain
      // column words'; every control sets its own (FilterSelect `column`).
      className="hidden xl:grid result-grid px-4 border border-transparent py-1.5 text-[10px] font-black uppercase tracking-wider text-fg-muted"
    >
      {/* The lead cell, over the compare and provider columns (index.css):
          the audience — the visitor's segment has no column —, its text on
          the checkboxes' left edge (`edge="lead"`) and never marked as set
          ("wissen" leaves it alone, the filter count leaves it out), then
          "✕ Wissen". Its short name (`table.audience_short`,
          EN "Audience"): with the English "Target audience" the control and
          "✕ Clear" took 242 px of the 227 px cell at a 1280 px window with a
          classic scrollbar and ran 5 px into "NET PER YEAR*"; the Dutch
          "Doelgroep: Particulier" + "✕ Wissen" take 226 (measured
          2026-10-08). normal-case + tracking-normal: a button inherits the
          line's case and letter spacing (preflight). */}
      <div className="rg-lead flex items-center gap-1.5 min-w-0 normal-case tracking-normal">
        <span className="sr-only">{t('table.col_compare')}</span>
        <span className="sr-only">{t('table.provider')}</span>
        <FilterSelect
          variant="column"
          edge="lead"
          name={t('table.audience_short')}
          label={fillTemplate(t('table.filter_pill_label'), { name: t('table.audience_short') })}
          markSet={false}
          {...audience}
        />
        {clearControl}
      </div>
      <div className="rg-net min-w-0">{t('table.net_per_year_short')}</div>
      <div className="rg-fee min-w-0">{t('table.fee')}</div>
      <div className="rg-dur min-w-0 flex items-center">
        <FilterSelect variant="column" name={t('table.duration')} {...duration} />
      </div>
      <div className="rg-pay min-w-0">{t('table.payout_frequency_short')}</div>
      {terms ? (
        <div className="rg-terms min-w-0 flex items-center">
          {/* The note the ⓘ used to open is the foot of the word's menu. */}
          <FilterSelect
            variant="column"
            name={t('table.col_terms')}
            describedBy={TERMS_INFO_ID}
            footer={() => (
              <p className="flex items-start gap-2 px-4 py-3 text-xs font-medium leading-relaxed text-slate-300">
                <Info className="mt-0.5 w-3.5 h-3.5 shrink-0 text-fg-muted" aria-hidden="true" />
                {termsNote}
              </p>
            )}
            {...terms}
          />
          {/* The same note as text, so it is in the baked DOM (the menu is
              mounted only while open). aria-hidden: it is the trigger's
              description already, and a referenced node is read all the same. */}
          <span id={TERMS_INFO_ID} className="sr-only" aria-hidden="true">
            {termsNote}
          </span>
        </div>
      ) : (
        <div className="rg-terms min-w-0 flex items-center gap-1">
          <span>{t('table.col_terms')}</span>
          {/* A toggletip: the button opens the note below it, a second press,
              Escape or a click elsewhere closes it. Named by the note it
              shows, so a screen reader hears it on focus. */}
          <span ref={termsTipRef} className="relative inline-flex">
            <button
              ref={termsButtonRef}
              type="button"
              aria-label={termsNote}
              aria-expanded={termsNoteOpen}
              aria-controls={TERMS_NOTE_ID}
              onClick={() => setTermsNoteOpen((open) => !open)}
              className={cn(
                'inline-flex min-h-6 shrink-0 items-center justify-center rounded-sm focus-ring hover:text-slate-200 transition-colors',
                termsNoteOpen && 'text-slate-200'
              )}
            >
              <Info className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
            {termsNoteOpen && (
              <div
                id={TERMS_NOTE_ID}
                role="note"
                className="absolute right-0 top-full z-40 mt-1.5 w-72 rounded-xl border border-slate-700/60 bg-slate-800 p-3 text-xs font-medium normal-case leading-relaxed tracking-normal text-slate-200 shadow-2xl shadow-slate-950/50"
              >
                {termsNote}
              </div>
            )}
          </span>
          {/* The same note as text, so it is in the baked DOM (an attribute is not
              text to a crawler, and the note is mounted only while open). Not
              `hidden`; aria-hidden because the button's name already carries it. */}
          <span className="sr-only" aria-hidden="true">
            {termsNote}
          </span>
        </div>
      )}
      <div className="rg-smart min-w-0 flex items-center">
        {/* The explanation the ⓘ opened is a button at the foot of the menu:
            it closes the menu (the focus back on the word, where the modal
            returns it) and opens the modal. */}
        <FilterSelect
          variant="column"
          name={t('table.col_smart_charger')}
          footer={(close) => (
            <button
              type="button"
              onClick={() => {
                close();
                onSmartChargerInfo();
              }}
              className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-xs font-semibold text-emerald-300 transition-colors hover:bg-slate-700/60 focus-visible:bg-slate-700/60"
            >
              <Info className="mt-px w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t('table.smart_charging_info_aria')}
            </button>
          )}
          {...smartCharging}
        />
      </div>
      {/* The sort control, over the two action columns (index.css places the
          cell from the cta column's start to the details column's end): its
          right edge on the Details column's, its chevron over the rows'
          Details chevrons. It says what it is — "Sorteren: <order>" — so it
          needs no group around it to be told apart from the filters (until
          2026-10-08 a group named "Sorteren" did that for screen readers
          only). normal-case + tracking-normal, as in the lead cell. */}
      <div className="rg-sort flex items-center justify-end min-w-0 normal-case tracking-normal">
        {sortControl}
      </div>
    </div>
  );
};
